import { useMemo } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import {
	useDailyBriefNudgeAction,
	DAILY_BRIEF_NUDGE_RECENCY_MS,
} from '@confluence/page-rovo-config-common/entry-points/useDailyBriefNudgeAction.ts';
import { useInsightsNudgeAction } from '@confluence/page-rovo-config-common/entry-points/useInsightsNudgeAction.ts';
import { useConvoStartersNudgeAction } from '@confluence/page-rovo-config-common/entry-points/useConvoStartersNudgeAction.ts';

import type { DailyBriefUnread } from '@atlassian/ai-smart-button-api/ai-smart-button-store';

import {
	isNudgeByAction,
	type NudgeEntry,
} from '@confluence/page-rovo-config-common/entry-points/nudgeTypes.ts';

/** Minimum read time (in minutes) required for the convo starter nudge. */
const CONVO_STARTER_MIN_READ_TIME_MINUTES = 2;

/**
 * Returns the list of nudge actions to register for the current page.
 * Each nudge declares its own enabled condition, including per-nudge read time thresholds.
 * Returns [] if the page is not base-eligible for nudges.
 * The returned array is ready to pass directly to setNudges.
 */
export function useViewPageNudgeConfig(
	isBaseEligible: boolean,
	readTimeMinutes: number | null,
	isCurrentUserContributor: boolean,
	isFirstVisit: boolean,
	dailyBriefStatus: DailyBriefUnread,
): ConversationAction[] {
	const viewPageRovoActions = useViewPageRovoActions();
	const dailyBriefNudgeAction = useDailyBriefNudgeAction(dailyBriefStatus?.conversationId ?? null);
	const insightsNudgeAction = useInsightsNudgeAction();
	const convoStartersNudgeAction = useConvoStartersNudgeAction();
	const { eligibleForChangeboarding } = useAudioButtonWrapperStore();

	const actionsById = useMemo(
		() =>
			new Map<string, ConversationAction>(
				viewPageRovoActions.map(
					(registeredAction: { actionId: string; action: ConversationAction }) => [
						registeredAction.actionId,
						registeredAction.action,
					],
				),
			),
		[viewPageRovoActions],
	);

	return useMemo(() => {
		if (!isBaseEligible) return [];

		const nudgeEntries: NudgeEntry[] = [
			{ actionId: 'page-catchup', enabled: true },
			{ actionId: 'audio-briefing', enabled: !eligibleForChangeboarding },
			{
				action: dailyBriefNudgeAction,
				enabled:
					!!dailyBriefStatus?.conversationId &&
					!!dailyBriefStatus?.completedAt &&
					Date.now() - new Date(dailyBriefStatus.completedAt).getTime() <
						DAILY_BRIEF_NUDGE_RECENCY_MS &&
					fg('rovo_button_daily_brief_nudge'),
			},
			// Insights nudge — 1 min read time threshold covered by isBaseEligible
			{
				action: insightsNudgeAction,
				enabled: expVal('rovo_growth_chat_pulse', 'isEnabled', false),
			},
			// Convo starter nudge — requires higher read time than the base threshold
			// Excluded for contributors and first-time visitors
			{
				action: convoStartersNudgeAction,
				enabled:
					!isCurrentUserContributor &&
					!isFirstVisit &&
					readTimeMinutes !== null &&
					readTimeMinutes > CONVO_STARTER_MIN_READ_TIME_MINUTES &&
					fg('aix_convo_starter_nudge_enabled'),
			},
			// Action items nudge — eligibility gated in useViewPageRovoActions
			{
				actionId: 'action-items-nudge',
				enabled: true,
			},
			{
				actionId: 'status-updates-automation',
				enabled: fg('cc_automations_discovery_nudges'),
			},
		];

		return nudgeEntries.reduce<ConversationAction[]>((acc, nudgeEntry) => {
			if (!nudgeEntry.enabled) return acc;
			const action = isNudgeByAction(nudgeEntry)
				? nudgeEntry.action
				: actionsById.get(nudgeEntry.actionId);
			if (action) acc.push(action);
			return acc;
		}, []);
	}, [
		isBaseEligible,
		readTimeMinutes,
		eligibleForChangeboarding,
		isCurrentUserContributor,
		isFirstVisit,
		dailyBriefStatus,
		dailyBriefNudgeAction,
		insightsNudgeAction,
		convoStartersNudgeAction,
		actionsById,
	]);
}
